import type { TrophyId, TrophyTier } from '@crowdfpl/domain/gamification';

/** Short tagline under the trophy title (PRD card header). */
export const TROPHY_TAGLINE_COPY: Record<TrophyId, string> = {
  crowd_slayer: 'Back the underdog and watch them win.',
  captain_assassin: 'Beat the Crowd Captain, week after week.',
  iron_manager: 'Never miss a gameweek.',
  perfect_week: 'Ten correct calls in a single gameweek.',
  last_believer: 'The only manager who backed the winner.',
  giant_leap: 'A huge Duels Rating swing in one gameweek.',
  early_bird: 'Correct calls locked in five days early.',
};

/** Trophy identity emoji (Lovable prototype parity). */
export const TROPHY_EMOJI: Record<TrophyId, string> = {
  crowd_slayer: '🏆',
  captain_assassin: '🧢',
  iron_manager: '🛡️',
  perfect_week: '⭐',
  last_believer: '👤',
  giant_leap: '🚀',
  early_bird: '🐦',
};

/** Per-trophy accent mapped onto existing semantic tokens (no new hex). */
export const TROPHY_ACCENT: Record<TrophyId, string> = {
  crowd_slayer: 'hsl(var(--gold))',
  captain_assassin: 'hsl(var(--competitive-blue))',
  iron_manager: 'hsl(var(--vote-seg-left))',
  perfect_week: 'hsl(var(--gold-light))',
  last_believer: 'hsl(var(--trophy-legendary))',
  giant_leap: 'hsl(var(--chart-5))',
  early_bird: 'hsl(var(--chart-4))',
};

/** Ladder labels under segments, one per tier. */
export const TROPHY_TIER_EMOJI: Record<TrophyTier, string> = {
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇',
  platinum: '💎',
  legendary: '👑',
  mythic: '🌌',
};

/** How-to-earn rules shown in the conditions disclosure. */
export const TROPHY_CONDITIONS: Record<TrophyId, string[]> = {
  crowd_slayer: [
    'Duel has at least 10 votes',
    'Your pick had 20% or less of the votes',
    'Your pick won',
  ],
  captain_assassin: [
    'Counted per gameweek',
    'Your captain outscores the Crowd Captain',
    'Streak breaks when the Crowd Captain scores more',
  ],
  iron_manager: [
    'Vote in at least 1 eligible duel per gameweek',
    'Counted across consecutive gameweeks',
  ],
  perfect_week: [
    'At least 10 counted duels in one gameweek',
    'Every counted prediction correct',
    'Only duels with 10+ votes count',
  ],
  last_believer: [
    'Duel has at least 10 votes',
    'The winner was the underdog',
    'You were the only manager who picked them',
  ],
  giant_leap: ['Net Duels Rating earned within one gameweek'],
  early_bird: [
    'Only duels with 10+ votes count',
    'Vote placed at least 5 days before the deadline',
    'Prediction must be correct',
  ],
};

/** Unit label for Next-tier requirement lines (`Next: Platinum · 50 wins`). */
export const TROPHY_METRIC_UNIT: Record<TrophyId, string> = {
  crowd_slayer: 'wins',
  captain_assassin: 'GW',
  iron_manager: 'GW',
  perfect_week: '',
  last_believer: '',
  giant_leap: 'pts',
  early_bird: 'calls',
};

export function formatTrophyTitle(trophyId: TrophyId): string {
  return trophyId
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Whole percent for rarity chips (14.29 → 14). */
export function formatRarityPercent(pct: number): number {
  return Math.trunc(pct);
}
